/**
 * Render queue + download (§6 of the RENDER card).
 *
 * Clips are rendered ONE AT A TIME on purpose: every render holds a decoder, an encoder and a
 * canvas alive, and the plan's whole memory story ("flat at ~160-170 MB") only holds when one
 * clip is in flight. The queue is the thing that makes that true, and it is also what gives the
 * UI its "Rendering 3 of 12 clips…" progress line.
 */
import type {
  AudioTrackMode,
  CapabilityReport,
  ClipRenderer,
  OverlayConfig,
  RenderResult,
} from './types';
import { WebCodecsRenderer } from './webcodecs';
import { ServerRenderer, type ServerRenderRequestMeta } from './server';
import { probeCapabilities } from './probe';

export interface RenderQueueItem {
  clipId: string;
  title: string;
  tIn: number;
  tOut: number;
}

export type RenderItemStatus = 'queued' | 'rendering' | 'complete' | 'failed';

export interface RenderItemResult {
  clipId: string;
  title: string;
  status: RenderItemStatus;
  result?: RenderResult;
  error?: string;
}

export interface RenderQueueProgress {
  /** 1-based position of the clip being rendered. */
  index: number;
  total: number;
  /** Clip being rendered. */
  clipId: string;
  title: string;
  /** 0..1 within the current clip (frame-count based). */
  clipFraction: number;
  /** Human line for the UI: "Rendering 3 of 12 clips…". */
  label: string;
}

export interface RenderQueueOptions {
  items: RenderQueueItem[];
  renderer: ClipRenderer;
  /** The branded overlay for one clip. */
  overlayFor: (item: RenderQueueItem) => OverlayConfig;
  sourceVideo: Blob | string;
  quality: 'preview' | 'export';
  audioTrack?: AudioTrackMode;
  onProgress?: (progress: RenderQueueProgress) => void;
  onItemDone?: (item: RenderItemResult) => void;
}

export function renderProgressLabel(index: number, total: number): string {
  return `Rendering ${index} of ${total} clip${total === 1 ? '' : 's'}…`;
}

/**
 * Renders every item in order. One clip's failure never takes the rest with it: the failure is
 * recorded against that clip and the queue moves on.
 */
export async function runRenderQueue(options: RenderQueueOptions): Promise<RenderItemResult[]> {
  const { items, renderer, overlayFor, sourceVideo, quality, audioTrack, onProgress, onItemDone } = options;
  const results: RenderItemResult[] = items.map((item) => ({
    clipId: item.clipId,
    title: item.title,
    status: 'queued' as RenderItemStatus,
  }));

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    const record = results[index];
    record.status = 'rendering';
    onProgress?.({
      index: index + 1,
      total: items.length,
      clipId: item.clipId,
      title: item.title,
      clipFraction: 0,
      label: renderProgressLabel(index + 1, items.length),
    });

    try {
      const result = await renderer.render(
        {
          sourceVideo,
          tIn: item.tIn,
          tOut: item.tOut,
          overlay: overlayFor(item),
          format: 'mp4',
          quality,
          audioTrack,
        },
        {
          onProgress: ({ frames, totalFrames }) => {
            onProgress?.({
              index: index + 1,
              total: items.length,
              clipId: item.clipId,
              title: item.title,
              clipFraction: totalFrames ? Math.min(1, frames / totalFrames) : 0,
              label: renderProgressLabel(index + 1, items.length),
            });
          },
        },
      );
      record.result = result;
      record.status = 'complete';
    } catch (error) {
      record.status = 'failed';
      record.error = error instanceof Error ? error.message : String(error);
    }
    onItemDone?.({ ...record });
  }

  return results;
}

/** Triggers a real browser download of one clip's MP4 Blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke late: Safari aborts a download whose object URL is revoked immediately.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export interface ZipEntry {
  name: string;
  blob: Blob;
}

/**
 * ZIP of every clip (§6 — batch download). JSZip is imported lazily so the bundle only pays for
 * it when a coach actually asks for the batch.
 */
export async function buildZipBlob(entries: ZipEntry[]): Promise<Blob> {
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  for (const entry of entries) zip.file(entry.name, entry.blob);
  return zip.generateAsync({ type: 'blob', compression: 'STORE' });
}

/** `round-3-highlights-01-line-break.mp4`, safe for every filesystem. */
export function clipFilename(index: number, title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  return `callout-${String(index + 1).padStart(2, '0')}-${slug || 'clip'}.mp4`;
}

/**
 * §2.3 adapter selection. Runs the probe, then hands back the renderer the session must use:
 * WebCodecs when the ladder found an H.264 encoder, the server Worker otherwise.
 */
export async function selectRenderer(
  options: { token?: string | null; server?: ServerRenderRequestMeta } = {},
): Promise<{ renderer: ClipRenderer; report: CapabilityReport }> {
  const report = await probeCapabilities();
  if (report.renderer === 'webcodecs') {
    return { renderer: new WebCodecsRenderer(report), report };
  }
  return {
    renderer: new ServerRenderer({ token: options.token ?? null, ...options.server }),
    report,
  };
}
